/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import GenericButton from "@ui-components/Button/GenericButton";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { mapFetchedDataToPortDetails } from "kontrolEdge/constants/constants";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/IconEdit_KE.svg";
import { ReactComponent as IconCheck } from "../../../../shared/assets/Icons/IconCheckSquared.svg";
import { ReactComponent as IconCancel } from "../../../../shared/assets/Icons/IconCrossSquare.svg";
import { updatePortNameTagAPI } from "kontrolEdge/components/Port/requests/request";
import ProvisioningDetailsModal, { SelectedPortSummary } from "kontrolEdge/components/Modals/ProvisioningDetails";
import { useLocation, useSearchParams } from "react-router-dom";
import RedundancySLAModal from "kontrolEdge/components/Modals/RedundancySLA";
import { useProcessingOverlay } from "@hooks/useProcessingOverlay";
import GenericModal from "@ui-components/Modal/GenericModal";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import StatusTable from "./StatusTable";
import PortConnections from "./PortConnections/PortConnections";
import { usePortDetails } from "./usePortDetails";
import { Port } from "kontrolEdge/views/OverviewComponents/Types";
import { getTextColor } from "kontrolEdge/helpers/getTextColor";
import { useAppSelector } from "@redux/hooks";
import { RootState } from "@redux/store/store";
import { PortPerformanceData } from "@redux/slices/kontrolEdge/portSlice";
import BandwidthChartUniversal from "kontrolEdge/components/Shared/Bandwidth/BandwidthChartUniversal";

export interface PortDetailItem {
  label: string;
  value?: string | number | boolean | React.ReactNode;
  type?: string;
  variant?: string;
}

export interface PortDetailSection {
  header: string;
  isCollapsible?: boolean;
  initialExpanded?: boolean;
  items: PortDetailItem[];
}

type LocationState = {
  selectedPortRow?: Partial<Pick<Port, "id" | "portId" | "customer" | "portType">>;
  portType?: "physical" | "virtual";
};

function getLatestInbound(pd?: PortPerformanceData | null) {
  const inbound = pd?.bandwidthUtilization?.inbound;
  if (!inbound?.length) return null;
  return inbound.reduce((prev, curr) =>
    new Date(curr.time).getTime() > new Date(prev.time).getTime() ? curr : prev
  );
}

const PortDetails: React.FC = () => {
  const [searchParams] = useSearchParams();
  //Port id selected from MainPort
  const portId = searchParams.get("portId");
  const subid = searchParams.get("subid") ?? undefined;
  const api = useAxiosAuth0();
  const userProfile = useAppSelector((state: RootState) => state.userProfile);
  const { canAccessAlerts } = userProfile;

  const portPerf = useAppSelector((s) => s.port.performanceData);
  const latestInbound = useMemo(() => getLatestInbound(portPerf), [portPerf]);


  const [refresh, setRefresh] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  // Data Layer: fetch core + extras
  const { raw, loading } = usePortDetails(
    portId,
    api,
    refresh
  );

  const location = useLocation();
  const { selectedPortRow, portType: statePortType } = (location.state as LocationState) ?? {};

  const fetchedPortType =
  (raw as any)?.portType ??
  (raw as any)?.kontrolEdgePort?.portType ??
  undefined;

  const effectivePortType = (statePortType ??
    selectedPortRow?.portType ??
    fetchedPortType) as "physical" | "virtual" | undefined;

  const selectedPortSummary = useMemo<SelectedPortSummary | null>(() => {
    const portIdStr =
      selectedPortRow?.portId ??
      (portId ?? undefined);
    if (!portIdStr) return null;
  
    const customerName =
      selectedPortRow?.customer?.name ??
      (raw as any)?.customer?.name ??
      undefined;
  
    return {
      portId: portIdStr,
      customer: customerName ? { name: customerName } : undefined,
    };
  }, [selectedPortRow, raw, portId]);

    // Derive detail sections
    const sections = useMemo(
      () =>
        raw
          ? mapFetchedDataToPortDetails(
              raw,
              selectedPortRow?.portId ?? undefined,
              subid
            )
          : [],
      [raw, selectedPortRow, subid]
    );

  //Edit name and tag inside the component
  const [isMembersModalOpen, setIsMembersModalOpen] = useState(false);
  const [membersModalText, setMembersModalText] = useState("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [originalName, setOriginalName] = useState("Port Test");
  const [isEditingTag, setIsEditingTag] = useState(false);
  const [originalTag, setOriginalTag] = useState("Tag Testing");
  const nameRef = useRef<HTMLInputElement>(null);
  const tagRef = useRef<HTMLInputElement>(null);

  const { tenantId } = useAppSelector((state) => state.selection);

  const overlaySpinner = useProcessingOverlay(loading);

  const [nameValue, setNameValue] = useState("");
  const [tagValue, setTagValue] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [RedundancyOpen, setRedundancyOpen] = useState(false);

  useEffect(() => {
    if (!raw) return;
    const serverName = raw.name ?? "";
    const serverTag = raw.tag ?? "";

    setNameValue(serverName);
    setTagValue(serverTag);
    setOriginalName(serverName);
    setOriginalTag(serverTag);
    // exit edit modes
    setIsEditingName(false);
    setIsEditingTag(false);
  }, [raw]);

  // Save Name/Tag
  const handleSaveNameTag = async () => {
    if (!portId) return;
    setIsSaving(true);
    try {
      await updatePortNameTagAPI(api, portId, {
        name: nameValue,
        tag: tagValue,
      });
      //Trigger a refetch next…
      setRefresh((r) => r + 1);
    } catch (err) {
      console.error("Error saving name/tag:", err);
    } finally {
      setIsEditingName(false);
      setIsEditingTag(false);
      setIsSaving(false);
    }
  };

  const handleCancelEditName = () => {
    setNameValue(originalName);
    setIsEditingName(false);
  };

  const handleCancelEditTag = () => {
    setTagValue(originalTag);
    setIsEditingTag(false);
  };
  const initialOverlay = useProcessingOverlay(loading && refresh === 0);
  const saveOverlay = useProcessingOverlay(isSaving);

  if (!portId) return <p>No port selected.</p>;
  if (loading && refresh === 0) return overlaySpinner;
  if (!raw) return <p>Port not found.</p>;

  return (
    <>
      {initialOverlay}
      {saveOverlay}
      {/* Modals */}
      <ProvisioningDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        portDetailsData={sections}
        rawPortData={raw}
        selectedPort={selectedPortSummary}
      />
      <RedundancySLAModal
        isOpen={RedundancyOpen}
        onClose={() => setRedundancyOpen(false)}
        portDetailsData={sections}
        rawPortData={raw}
      />
      <GenericModal
        isOpen={isMembersModalOpen}
        onClose={() => setIsMembersModalOpen(false)}
        title="Members List"
        size="md"
      >
        <p className="text-sm text-semantic-text-secondary">
          {membersModalText}
        </p>
      </GenericModal>
      {/*Port detail cards section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
        {sections.map((card, index) => (
          <div key={index} className="flex w-full self-start">
            <GenericCard
              //Validation for the 2 first cards and if there's data in LAG
              isCollapsible={card.isCollapsible ?? index >= 2}
              initialExpanded={card.initialExpanded}
              headerContent={card.header}
              rightContent={
                index === 0 ? (
                  <GenericButton
                    label="Provisioning Details"
                    variant="secondary"
                    shape="ghost"
                    size="sm"
                    onClick={() => setIsModalOpen(true)}
                  />
                ) : index === 1 ? (
                  <GenericTag
                    label={
                      latestInbound
                        ? (() => {
                            const d = new Date(latestInbound.time);
                            // format date as DD/MM/YYYY
                            const datePart = d.toLocaleDateString("es-MX", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              timeZone: "UTC",
                            });
                            // format time as HH:MM:SS
                            const timePart = d.toLocaleTimeString("en-US", {
                              hour: "2-digit",
                              minute: "2-digit",
                              second: "2-digit",
                              hour12: false,
                              timeZone: "UTC",
                            });
                            return `${datePart} ${timePart} UTC`;
                          })()
                        : "-"
                    }
                  />
                ) : null
              }
              bodyContent={
                <>
                  {card.items.map((item, itemIndex) => (
                    <React.Fragment key={itemIndex}>
                      {/* {card.header === "Status" &&
												item.type === "subtitle" &&
												item.label === "Subscription Details" && (
													<div className="flex justify-center w-full py-2">
														{/* <GenericButton
															label="SLA & Redundancy"
															variant="secondary"
															shape="ghost"
															size="sm"
															onClick={() => {
																setRedundancyOpen(true);
															}}
														/> *
													</div>
												)} */}
                      <div
                        key={itemIndex}
                        className={`flex justify-between w-full py-1 ${
                          itemIndex !== card.items.length - 1 ? "border-b" : ""
                        }`}
                      >
                        {card.header === "LAG Details" &&
                        item.label === "Members" ? (
                          //This section handles the large amount of members
                          <div className="flex justify-between w-full">
                            <h3 className="text-sm text-semantic-text-primary">
                              {item.label}
                            </h3>
                            <p className="text-sm text-semantic-text-secondary">
                              {(() => {
                                const membersArray = String(item.value)
                                  .split(",")
                                  .map((s) => s.trim())
                                  .filter(Boolean);
                                // Show the first 2 members.
                                const visibleMembers = membersArray.slice(0, 2);
                                // Determine if there are remaining members.
                                const remainingMembers = membersArray.slice(2);
                                return (
                                  <>
                                    {visibleMembers.join(", ")}
                                    {remainingMembers.length > 0 && (
                                      <>
                                        {", "}
                                        <span
                                          className="cursor-pointer text-blue-500"
                                          onClick={() => {
                                            // Set the modal text to the full members list.
                                            setMembersModalText(
                                              membersArray.join(", ")
                                            );
                                            setIsMembersModalOpen(true);
                                          }}
                                        >
                                          ...
                                        </span>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </p>
                          </div>
                        ) : card.header === "Port Details" &&
                          //This section handles the editing logic for tag and name
                          (item.label === "Name" || item.label === "Tag") ? (
                          <div className="flex justify-between w-full">
                            <h3 className="text-sm text-semantic-text-primary">
                              {item.label}
                            </h3>
                            {/* // Editing logic for "Name" */}
                            {item.label === "Name" ? (
                              <div className="flex items-center space-x-2">
                                {isEditingName ? (
                                  <>
                                    <button onClick={handleSaveNameTag}>
                                      <IconCheck className="w-5 h-5 text-semantic-text-secondary" />
                                    </button>
                                    <button onClick={handleCancelEditName}>
                                      <IconCancel className="w-5 h-5 text-semantic-text-secondary" />
                                    </button>
                                    <input
                                      ref={nameRef}
                                      type="text"
                                      value={nameValue}
                                      onChange={(e) =>
                                        setNameValue(e.target.value)
                                      }
                                      className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setIsEditingName(true)}
                                      className="text-gray-500 hover:text-gray-700"
                                    >
                                      <IconEdit className="w-4 h-4" />
                                    </button>
                                    <GenericTag
                                      label={nameValue}
                                      variant="neutral"
                                      size="small"
                                      minWidth="100px"
                                      className="text-center"
                                    />
                                  </>
                                )}
                              </div>
                            ) : (
                              // Editing logic for "Tag"
                              <div className="flex items-center space-x-2">
                                {isEditingTag ? (
                                  <>
                                    <button onClick={handleSaveNameTag}>
                                      <IconCheck className="w-5 h-5 text-semantic-text-secondary" />
                                    </button>
                                    <button onClick={handleCancelEditTag}>
                                      <IconCancel className="w-5 h-5 text-semantic-text-secondary" />
                                    </button>
                                    <input
                                      ref={tagRef}
                                      type="text"
                                      value={tagValue}
                                      onChange={(e) =>
                                        setTagValue(e.target.value)
                                      }
                                      className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                                    />
                                  </>
                                ) : (
                                  <>
                                    <button
                                      onClick={() => setIsEditingTag(true)}
                                      className="text-gray-500 hover:text-gray-700"
                                    >
                                      <IconEdit className="w-4 h-4" />
                                    </button>
                                    <GenericTag
                                      label={tagValue}
                                      variant="neutral"
                                      size="small"
                                      minWidth="100px" className="text-center"
                                    />
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Subtitle Handling */}
                            {item.type === "subtitle" ? (
                              <h4 className="text-right text-sm font-medium text-semantic-text-secondary py-3">
                                {item.label}
                              </h4>
                            ) : (
                              <div className="flex justify-between w-full">
                                <h3 className="text-sm text-semantic-text-primary">
                                  {item.label}
                                </h3>
                                {item.type === "tag" && item.value ? (
                                  <GenericTag
                                    label={String(item.value)}
                                    variant="success"
                                  />
                                ) : item.type === "button" && item.value ? (
                                  <GenericButton
                                    label={item.value}
                                    variant="secondary"
                                    shape="ghost"
                                    size="sm"
                                  />
                                ) : item.value ? (
                                  <p
                                    className={`text-sm ${getTextColor(
                                      item.label,
                                      String(item.value)
                                    )}`}
                                  >
                                    {item.value}
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-500">-</p>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </>
              }
            />
          </div>
        ))}
      </div>
      {/* Port Utilization chart */}
      <BandwidthChartUniversal
        service="port"
        id={portId}
        tenantId={tenantId}
        portType={effectivePortType}
      />
      {/* Associated connections section */}
      <PortConnections portId={portId} />
      {/* Activity Section */}
      <StatusTable
        data={raw?.statusHistory}
        headerContent="Activity"
        rightContent=""
      />
      {/* {canAccessAlerts && portId && (
        <NotificationsView serviceType="port" serviceId={portId} />
      )} */}
    </>
  );
};

export default PortDetails;
